/**
 * Voice Activity Detection (VAD) Module
 *
 * Simple energy-based VAD using Web Audio API.
 * Less aggressive than neural network VADs, better for natural conversation.
 *
 * How it works:
 * 1. Analyzes audio energy (RMS) in real-time
 * 2. Detects speech when energy exceeds threshold
 * 3. Detects silence when energy drops below threshold for a duration
 * 4. Emits speech_start / speech_end events
 */

export interface VADConfig {
  energyThreshold: number;
  speechStartDelay: number;
  speechEndDelay: number;
  smoothing: number;
}

export type VADState = "silence" | "speech";

export interface VADEvents {
  onSpeechStart?: () => void;
  onSpeechEnd?: () => void;
  onStateChange?: (state: VADState) => void;
  onEnergy?: (energy: number) => void;
}

const DEFAULT_CONFIG: VADConfig = {
  energyThreshold: 0.02,
  speechStartDelay: 80,
  speechEndDelay: 400,
  smoothing: 0.9,
};

export class VoiceActivityDetector {
  private config: VADConfig;
  private events: VADEvents;
  private state: VADState = "silence";
  private smoothedEnergy: number = 0;
  private speechStartTimer: ReturnType<typeof setTimeout> | null = null;
  private speechEndTimer: ReturnType<typeof setTimeout> | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Float32Array<ArrayBufferLike> | null = null;
  private animationFrame: number | null = null;
  private isActive: boolean = false;

  constructor(config: Partial<VADConfig> = {}, events: VADEvents = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.events = events;
  }

  async initialize(stream: MediaStream): Promise<void> {
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);

    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = this.config.smoothing;

    source.connect(this.analyser);

    this.dataArray = new Float32Array(this.analyser.frequencyBinCount);
  }

  start(): void {
    if (this.isActive) return;
    this.isActive = true;
    this.state = "silence";
    this.smoothedEnergy = 0;
    this.tick();
  }

  stop(): void {
    this.isActive = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.clearTimers();
    this.state = "silence";
  }

  private clearTimers(): void {
    if (this.speechStartTimer) {
      clearTimeout(this.speechStartTimer);
      this.speechStartTimer = null;
    }
    if (this.speechEndTimer) {
      clearTimeout(this.speechEndTimer);
      this.speechEndTimer = null;
    }
  }

  private tick = (): void => {
    if (!this.isActive || !this.analyser || !this.dataArray) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.analyser.getFloatTimeDomainData(this.dataArray as any);

    const energy = this.calculateRMS(
      this.dataArray as Float32Array<ArrayBufferLike>
    );

    this.smoothedEnergy =
      this.config.smoothing * this.smoothedEnergy +
      (1 - this.config.smoothing) * energy;

    this.events.onEnergy?.(this.smoothedEnergy);

    this.updateState(this.smoothedEnergy);

    this.animationFrame = requestAnimationFrame(this.tick);
  };

  private calculateRMS(data: Float32Array<ArrayBufferLike>): number {
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      sum += data[i] * data[i];
    }
    return Math.sqrt(sum / data.length);
  }

  private updateState(energy: number): void {
    const isSpeech = energy > this.config.energyThreshold;

    if (isSpeech) {
      if (this.speechEndTimer) {
        clearTimeout(this.speechEndTimer);
        this.speechEndTimer = null;
      }

      if (this.state === "silence" && !this.speechStartTimer) {
        this.speechStartTimer = setTimeout(() => {
          if (this.state === "silence") {
            this.state = "speech";
            this.events.onSpeechStart?.();
            this.events.onStateChange?.("speech");
          }
          this.speechStartTimer = null;
        }, this.config.speechStartDelay);
      }
    } else {
      if (this.speechStartTimer) {
        clearTimeout(this.speechStartTimer);
        this.speechStartTimer = null;
      }

      if (this.state === "speech" && !this.speechEndTimer) {
        this.speechEndTimer = setTimeout(() => {
          if (this.state === "speech") {
            this.state = "silence";
            this.events.onSpeechEnd?.();
            this.events.onStateChange?.("silence");
          }
          this.speechEndTimer = null;
        }, this.config.speechEndDelay);
      }
    }
  }

  getState(): VADState {
    return this.state;
  }

  getSmoothedEnergy(): number {
    return this.smoothedEnergy;
  }
}

export function createVAD(
  config: Partial<VADConfig> = {},
  events: VADEvents = {}
): VoiceActivityDetector {
  return new VoiceActivityDetector(config, events);
}
